import { inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DevicesDataService } from 'src/app/services/data/devices-data/devices-data.service';
import {
  chartHealthSegmentsColors,
  createDeviceBadgesFn
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  ListingDataSource,
  ListingPageModel
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from 'src/app/shared/components/listing-table/listing-table.component';
import { DeviceSidePanelTitleComponent } from '../../components/device-side-panel-title/device-side-panel-title.component';
import { DeviceSidePanelComponent } from '../../components/device-side-panel/device-side-panel.component';
import { devicesListingSource } from '../../constants/devices.constants';
import { DeviceListingFieldName, DevicesPayload, OrderDirection, TableDevices } from '../../components/devices.models';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TAB_VALUE } from 'src/app/shared/components/listing-page/listing-page.types';
import { ExportRow } from '@portal/shared/types/export.types';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageContext } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-context.types';
import { DevicesMapComponent } from '@portal/shared/components/map-report-group/map-report-group.component';
import { Applicable } from 'src/app/pages/administration/report-groups/report-groups.types';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { DcuListingFiltersSourceService } from './dcu-listing-filters-source.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { createDcuColumns } from './dcu-listing.columns';
import { DCU_DEFAULT_SEARCH_TYPE, DCU_SEARCH_TYPES, DCU_SORT_FIELD } from './dcu-listing.search';
import { mapDcuDeviceToExport } from './dcu-listing.export';

@Injectable()
export class DcuListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly devices = inject(DevicesDataService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly timezoneService = inject(TimezoneService);

  readonly pageTitle = TranslateRegistry.get.DCUS_BASE_STATIONS.TITLE.DCUS_BASE_STATIONS;
  readonly searchTypeList = signal(DCU_SEARCH_TYPES);
  readonly selectedSearchType = signal(DCU_DEFAULT_SEARCH_TYPE);
  readonly chartSegmentsColors = chartHealthSegmentsColors;
  readonly createBadgesFn = createDeviceBadgesFn;
  readonly sorting = signal({
    columnName: DeviceListingFieldName.LastHeard,
    orderDirection: OrderDirection.DESC
  });
  readonly tableColumns = signal(createDcuColumns());
  readonly dataSource: ListingDataSource<TableDevices> = {
    getAll: tableState => {
      return this.devices.prepareDataForTable(tableState);
    },
    total: 0
  };
  readonly exportConfig = {
    dataSource: {
      export: (payload: Pick<DevicesPayload, 'filters' | 'sort'>): Observable<ExportRow[]> => {
        return this.devices
          .export(payload)
          .pipe(
            map(response => response.map(dcu => mapDcuDeviceToExport(dcu, d => this.timezoneService.formatLong(d))))
          );
      }
    },
    fileName: 'Export-DCU'
  };
  readonly selectionFieldName = 'deviceUID';
  readonly mapCmp = DevicesMapComponent;
  readonly tableCmp = ListingTableComponent;
  readonly defaultTab = TAB_VALUE.Table;
  readonly sortField = DCU_SORT_FIELD;
  readonly sidePanelBodyCmp = DeviceSidePanelComponent;
  readonly sidePanelTitleCmp = DeviceSidePanelTitleComponent;
  readonly createMapCmpInputs = (ctx: ListingPageContext): Record<string, unknown> => {
    return {
      selectedLocationData: ctx.selectedLocation(),
      filtersPayload: ctx.mapFilters(),
      chartSegmentsColors: this.chartSegmentsColors,
      createBadgesFn: this.createBadgesFn,
      locationSelectedFn: ctx.onLocationSelected,
      applicable: Applicable.AmiHealthDashboard,
      actionSource: devicesListingSource,
      pageId: this.pageId
    };
  };
  readonly pageId = ListingPageId.Dcu;
  readonly filterSource = inject(DcuListingFiltersSourceService);

  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;
    listingPageModelRegistry.add(this.pageId, this);
  }
}
